import axios from 'axios'
import { API_CONFIG, AUTH_CONFIG } from '../shared/api/config'
// Phase B3: access token is in memory; ensureFreshToken also bootstraps it from the refresh
// cookie after a reload, so interceptors must go through it rather than reading storage.
import { ensureFreshToken } from '../shared/api/tokenRefresh'

const BASE_URL = API_CONFIG.BID_WORKUP.baseURL

const getAuthHeaders = async () => {
  const token = await ensureFreshToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const api = axios.create({
  baseURL: BASE_URL,
  timeout: API_CONFIG.BID_WORKUP.timeout,
})

api.interceptors.request.use(async (config) => {
  Object.assign(config.headers, await getAuthHeaders())
  return config
})

export const notificationsAPI = {
  /**
   * Get notifications for the current user
   * @param {Object} params - { unread_only, limit }
   */
  getAll: (params = {}) => {
    // The global API automatically uses the authenticated user's token
    return api.get('/notifications/', {
      params: { unread_only: params.unread_only },
    })
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: () => {
    return api.get('/notifications/count')
  },

  /**
   * Mark specific notifications as read
   * @param {number[]} notificationIds
   */
  markAsRead: async (notificationIds) => {
    // The global API marks read individually or read-all
    for (const id of notificationIds) {
      await api.post(`/notifications/${id}/read`).catch(() => {})
    }
    return { ok: true }
  },

  /**
   * Create an SSE connection for real-time notifications
   * @param {function} onMessage - callback for each notification event
   * @param {function} onError - callback for connection errors
   * @returns {EventSource} the SSE connection (call .close() to disconnect)
   */
  connectSSE: (onMessage, onError) => {
    // Phase B3: do NOT gate on an in-memory access token — right after a reload there is none
    // even though the session is valid, which would silently leave SSE disconnected for the rest
    // of the page's life. Gate on the persisted session instead; the ticket mint below is the
    // real authorization check and fails closed on its own.
    if (!localStorage.getItem(AUTH_CONFIG.USER_KEY)) return null

    // B2: put a short-lived TICKET in the EventSource URL instead of the full access token. A ticket is
    // minted over the authed (Bearer) channel, which EventSource itself cannot use. Because a ticket
    // expires in ~45s, we must NOT rely on the browser's native EventSource auto-reconnect — that
    // re-opens the SAME url and would loop forever on a dead ticket. Close and reconnect with a FRESH one.
    let eventSource = null
    let closed = false
    let retryTimer = null

    const open = async () => {
      if (closed) return
      let url
      try {
        const { data } = await api.post('/notifications/stream-ticket')
        url = `${BASE_URL}/notifications/stream?ticket=${encodeURIComponent(data.ticket)}`
      } catch {
        // Phase B3: the ?token= fallback is GONE — there is no longer a readable access token to
        // put in a URL, and B2 tickets have been live and verified. A failed mint now just retries
        // on the existing backoff below rather than degrading to a credential-in-URL.
        if (!closed) {
          clearTimeout(retryTimer)
          retryTimer = setTimeout(open, 5000)
        }
        return
      }
      if (closed) return

      eventSource = new EventSource(url)

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          onMessage(data)
        } catch {
          // heartbeat or non-JSON message
        }
      }

      eventSource.onerror = (err) => {
        if (onError) onError(err)
        try { eventSource.close() } catch { /* already closed */ }
        if (!closed) {
          clearTimeout(retryTimer)
          retryTimer = setTimeout(open, 5000)
        }
      }
    }

    open()

    // Handle mirrors the previous EventSource contract: callers only ever use .close().
    return {
      close: () => {
        closed = true
        clearTimeout(retryTimer)
        if (eventSource) { try { eventSource.close() } catch { /* noop */ } }
      },
    }
  },
}

export default notificationsAPI
